import { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, Send, Paperclip, X, ChevronLeft,
  FileText, Image, Download, Plus, RefreshCw, Loader, User,
} from 'lucide-react';
import {
  getInbox, getThread, createThread, sendMessage, getAttachmentUrl,
} from '../../services/messageService';
import { fetchAllUsers } from '../../services/adminService';
import authService from '../../services/authService';

// ── helpers ──────────────────────────────────────────────────────────────────

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const fmtSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const isImage = (mime = '') => mime.startsWith('image/');

const senderName = (sender) => {
  if (!sender) return 'Unknown';
  return [sender.firstName, sender.lastName].filter(Boolean).join(' ') || sender.username || sender.email || 'Unknown';
};

const isAdminRole = (role) => role === 'admin' || role === 'super_admin';

// ── sub-components ────────────────────────────────────────────────────────────

function AttachmentChip({ att, onRemove }) {
  const img = isImage(att.type || att.mimeType);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, fontSize: 11, color: '#d1d5db', maxWidth: 180 }}>
      {img ? <Image style={{ width: 13, height: 13, flexShrink: 0 }} /> : <FileText style={{ width: 13, height: 13, flexShrink: 0 }} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{att.name || att.originalName}</span>
      {onRemove && (
        <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9ca3af', lineHeight: 1 }}>
          <X style={{ width: 11, height: 11 }} />
        </button>
      )}
    </div>
  );
}

function AttachmentView({ att }) {
  const url = getAttachmentUrl(att.filename);
  const img = isImage(att.mimeType);
  if (img) {
    return (
      <a href={url} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: 6 }}>
        <img src={url} alt={att.originalName} style={{ maxWidth: 240, maxHeight: 180, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', objectFit: 'cover' }} />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" download={att.originalName} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '6px 10px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#93c5fd', fontSize: 12, textDecoration: 'none' }}>
      <FileText style={{ width: 13, height: 13 }} />
      {att.originalName}
      <span style={{ color: '#6b7280', fontSize: 10 }}>{fmtSize(att.size)}</span>
      <Download style={{ width: 11, height: 11 }} />
    </a>
  );
}

// ── Compose / reply box ───────────────────────────────────────────────────────

function ComposeBox({ onSend, placeholder = 'Write a message…', showSubject = false, disabled = false }) {
  const [body,    setBody]    = useState('');
  const [subject, setSubject] = useState('');
  const [files,   setFiles]   = useState([]);
  const [sending, setSending] = useState('');
  const fileRef = useRef();

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...picked].slice(0, 5));
    e.target.value = '';
  };

  const handleSend = async () => {
    if ((!body.trim() && files.length === 0) || sending) return;
    setSending('Sending…');
    try {
      await onSend(subject, body, files);
      setBody(''); setSubject(''); setFiles([]);
    } catch (err) {
      setSending('Error: ' + err.message);
      setTimeout(() => setSending(''), 3000);
      return;
    }
    setSending('');
  };

  const canSend = (body.trim() || files.length > 0) && !sending && !disabled;

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '12px 14px', flexShrink: 0 }}>
      {showSubject && (
        <input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder="Subject (optional)"
          style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '7px 10px', color: '#f1f5f9', fontSize: 12, marginBottom: 8, outline: 'none', boxSizing: 'border-box' }}
        />
      )}
      <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, overflow: 'hidden' }}>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder={placeholder}
          rows={3}
          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend(); }}
          style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', padding: '10px 12px', color: '#f1f5f9', fontSize: 13, resize: 'none', boxSizing: 'border-box' }}
        />
        {files.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '6px 10px 0' }}>
            {files.map((f, i) => (
              <AttachmentChip key={i} att={{ name: f.name, type: f.type }} onRemove={() => setFiles(prev => prev.filter((_, j) => j !== i))} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => fileRef.current?.click()} title="Attach file" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4, borderRadius: 6 }}
              onMouseEnter={e => e.currentTarget.style.color = '#9ca3af'}
              onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
            >
              <Paperclip style={{ width: 15, height: 15 }} />
            </button>
            <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.gif,.pdf,.ppt,.pptx,.doc,.docx" style={{ display: 'none' }} onChange={handleFiles} />
            {sending && <span style={{ fontSize: 11, color: sending.startsWith('Error') ? '#f87171' : '#9ca3af' }}>{sending}</span>}
          </div>
          <button
            onClick={handleSend}
            disabled={!canSend}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: canSend ? '#16a34a' : '#374151', color: '#fff', fontSize: 12, fontWeight: 700, cursor: canSend ? 'pointer' : 'not-allowed', opacity: canSend ? 1 : 0.5 }}
          >
            <Send style={{ width: 12, height: 12 }} />
            Send
          </button>
        </div>
      </div>
      <p style={{ margin: '5px 0 0', fontSize: 10, color: '#4b5563' }}>Ctrl+Enter to send · JPG, PNG, PDF, PPT, DOC (up to 5 files, 10 MB each)</p>
    </div>
  );
}

// ── Thread view ───────────────────────────────────────────────────────────────

function ThreadView({ threadId, myId, onBack }) {
  const [thread,   setThread]   = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const bottomRef = useRef();

  const load = useCallback(async () => {
    try {
      const data = await getThread(threadId);
      setThread(data.thread);
      setMessages(data.messages || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async (_subject, body, files) => {
    const data = await sendMessage(threadId, body, files);
    setMessages(prev => [...prev, data.message]);
    // Update thread preview
    setThread(prev => prev ? { ...prev, lastMessageAt: new Date().toISOString(), lastPreview: body.slice(0, 80) } : prev);
  };

  if (loading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader style={{ width: 22, height: 22, color: '#6b7280' }} className="animate-spin" /></div>;
  if (error)   return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171', fontSize: 13 }}>{error}</div>;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, borderRadius: 6 }}>
          <ChevronLeft style={{ width: 16, height: 16 }} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {thread?.subject || 'Conversation'}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: '#6b7280' }}>
            {thread?.participants?.map(p => senderName(p)).join(', ')}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.map(msg => {
          const mine = msg.sender?._id === myId || msg.sender === myId;
          const admin = isAdminRole(msg.sender?.role);
          return (
            <div key={msg._id} style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 8, alignItems: 'flex-end' }}>
              {/* Avatar */}
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: admin ? '#7c3aed' : '#065f46', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {(msg.sender?.firstName?.[0] || '?').toUpperCase()}
              </div>
              {/* Bubble */}
              <div style={{ maxWidth: '72%' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3, flexDirection: mine ? 'row-reverse' : 'row' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: admin ? '#c084fc' : '#4ade80' }}>{senderName(msg.sender)}</span>
                  <span style={{ fontSize: 10, color: '#4b5563' }}>{timeAgo(msg.createdAt)}</span>
                </div>
                <div style={{ padding: '9px 12px', borderRadius: mine ? '12px 4px 12px 12px' : '4px 12px 12px 12px', background: mine ? '#1e4d3f' : '#1e293b', border: `1px solid ${mine ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.07)'}` }}>
                  {msg.body && <p style={{ margin: 0, fontSize: 13, color: '#e2e8f0', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{msg.body}</p>}
                  {msg.attachments?.map((att, i) => <AttachmentView key={i} att={att} />)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply box */}
      <ComposeBox onSend={handleSend} placeholder="Reply…" />
    </div>
  );
}

// ── Inbox ─────────────────────────────────────────────────────────────────────

function Inbox({ onSelect, onCompose, refreshKey }) {
  const [threads, setThreads]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getInbox().then(d => { if (alive) { setThreads(d.threads || []); setLoading(false); } }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, [refreshKey]);

  if (loading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader style={{ width: 20, height: 20, color: '#6b7280' }} /></div>;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px 8px', flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>Inbox</span>
        <button onClick={onCompose} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#16a34a', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 700 }}>
          <Plus style={{ width: 12, height: 12 }} /> New
        </button>
      </div>

      {/* Thread list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {threads.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <MessageSquare style={{ width: 32, height: 32, color: '#374151', display: 'block', margin: '0 auto 12px' }} />
            <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>No messages yet</p>
            <p style={{ color: '#4b5563', fontSize: 12, margin: '4px 0 0' }}>Start a conversation with admin</p>
          </div>
        ) : threads.map(t => (
          <div
            key={t._id}
            onClick={() => onSelect(t._id)}
            style={{ padding: '11px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', background: t.unreadCount > 0 ? 'rgba(34,197,94,0.05)' : 'transparent', transition: 'background 0.1s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = t.unreadCount > 0 ? 'rgba(34,197,94,0.05)' : 'transparent'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: t.unreadCount > 0 ? 700 : 500, color: t.unreadCount > 0 ? '#f1f5f9' : '#d1d5db', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {t.subject || 'Support Request'}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {t.unreadCount > 0 && (
                  <span style={{ background: '#22c55e', color: '#fff', fontSize: 9, fontWeight: 800, minWidth: 16, height: 16, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{t.unreadCount}</span>
                )}
                <span style={{ fontSize: 10, color: '#4b5563' }}>{timeAgo(t.lastMessageAt)}</span>
              </div>
            </div>
            <p style={{ margin: '3px 0 0', fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {senderName(t.createdBy)} · {t.lastPreview || 'No preview'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── New thread compose ────────────────────────────────────────────────────────

function NewThread({ onCreated, onCancel, isAdmin }) {
  const [recipientId, setRecipientId] = useState('');
  const [users,       setUsers]       = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    setLoadingUsers(true);
    fetchAllUsers(1, 200)
      .then(d => setUsers((d.users || []).filter(u => u.role === 'user' || u.role === 'custodian')))
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, [isAdmin]);

  const handleSend = async (subject, body, files) => {
    if (isAdmin && !recipientId) throw new Error('Please select a recipient');
    await createThread(subject || 'Support Request', body, files, isAdmin ? recipientId : null);
    onCreated();
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, borderRadius: 6 }}>
          <ChevronLeft style={{ width: 16, height: 16 }} />
        </button>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>
          {isAdmin ? 'Message a User' : 'New Message to Admin'}
        </p>
      </div>

      {/* Admin: recipient picker */}
      {isAdmin && (
        <div style={{ padding: '12px 14px 0', flexShrink: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 6 }}>
            <User style={{ width: 12, height: 12 }} /> To (recipient) *
          </label>
          {loadingUsers
            ? <p style={{ fontSize: 12, color: '#6b7280' }}>Loading users…</p>
            : (
              <select
                value={recipientId}
                onChange={e => setRecipientId(e.target.value)}
                style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 10px', color: recipientId ? '#f1f5f9' : '#6b7280', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              >
                <option value="">— Select a user —</option>
                {users.map(u => (
                  <option key={u.id || u._id} value={u.id || u._id} style={{ background: '#1f2937', color: '#f1f5f9' }}>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.username} ({u.email}) [{u.role}]
                  </option>
                ))}
              </select>
            )
          }
          {users.length === 0 && !loadingUsers && (
            <p style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>No users or custodians found.</p>
          )}
        </div>
      )}

      <div style={{ flex: 1 }} />
      <ComposeBox onSend={handleSend} showSubject placeholder={isAdmin ? 'Write your message…' : 'Describe your report or request…'} />
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function MessagingPanel({ isAdmin: adminView, onUnreadChange }) {
  const [view,       setView]       = useState('inbox'); // 'inbox' | 'thread' | 'compose'
  const [threadId,   setThreadId]   = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const user = authService.getCurrentUser();

  const refresh = () => setRefreshKey(k => k + 1);

  const handleSelect = (id) => { setThreadId(id); setView('thread'); };
  const handleBack   = () => { setView('inbox'); setThreadId(null); refresh(); };
  const handleCreated = () => { setView('inbox'); refresh(); };

  // Notify parent of unread count
  useEffect(() => {
    if (!onUnreadChange) return;
    let alive = true;
    import('../../services/messageService').then(({ getUnreadCount }) => {
      getUnreadCount().then(d => { if (alive) onUnreadChange(d.count || 0); }).catch(() => {});
    });
    return () => { alive = false; };
  }, [refreshKey, onUnreadChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#111827', color: '#f1f5f9' }}>
      {/* Title bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 14px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessageSquare style={{ width: 16, height: 16, color: '#22c55e' }} />
          <span style={{ fontSize: 15, fontWeight: 700 }}>Messages</span>
        </div>
        <button onClick={refresh} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4, borderRadius: 6 }}>
          <RefreshCw style={{ width: 14, height: 14 }} />
        </button>
      </div>

      {view === 'inbox'   && <Inbox    onSelect={handleSelect} onCompose={() => setView('compose')} refreshKey={refreshKey} />}
      {view === 'thread'  && <ThreadView threadId={threadId} myId={user?.id} onBack={handleBack} />}
      {view === 'compose' && <NewThread onCreated={handleCreated} onCancel={() => setView('inbox')} isAdmin={!!adminView} />}
    </div>
  );
}
